/* PayPalSubscribeButton — renders a PayPal "Subscribe" button for a given plan_id.

   Loads the PayPal JS SDK once per page, then mounts a button inside the target
   container. The SDK reads the client-id from REACT_APP_PAYPAL_CLIENT_ID.

   Usage:
     <PayPalSubscribeButton planId="P-XXX" onApprove={sub => ...} />
*/
import React, { useEffect, useRef } from 'react';

const PAYPAL_SRC = (clientId) => (
    'https://www.paypal.com/sdk/js' +
    `?client-id=${encodeURIComponent(clientId)}` +
    '&vault=true' +
    '&intent=subscription'
);

let _sdkPromise = null;

const loadSdk = (clientId) => {
    if (!clientId) return Promise.reject(new Error('PayPal client ID missing'));
    if (window.paypal) return Promise.resolve(window.paypal);
    if (_sdkPromise) return _sdkPromise;
    _sdkPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = PAYPAL_SRC(clientId);
        s.setAttribute('data-sdk-integration-source', 'linguisticquest');
        s.onload = () => resolve(window.paypal);
        s.onerror = () => reject(new Error('Failed to load PayPal SDK'));
        document.head.appendChild(s);
    });
    return _sdkPromise;
};

export default function PayPalSubscribeButton({ planId, onApprove, onError, style }) {
    const ref = useRef(null);
    const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;

    useEffect(() => {
        let cancelled = false;
        if (!planId || !clientId || !ref.current) return;
        ref.current.innerHTML = '';
        loadSdk(clientId).then((paypal) => {
            if (cancelled || !ref.current) return;
            paypal.Buttons({
                style: style || { shape: 'pill', color: 'gold', layout: 'vertical', label: 'subscribe' },
                createSubscription: (data, actions) => actions.subscription.create({ plan_id: planId }),
                onApprove: (data) => {
                    if (onApprove) onApprove(data);
                },
                onError: (err) => {
                    console.error('PayPal subscribe error', err);
                    if (onError) onError(err);
                },
            }).render(ref.current).catch((e) => console.error('PayPal render error', e));
        }).catch((e) => {
            console.error(e);
            if (onError) onError(e);
        });
        return () => { cancelled = true; };
    }, [planId, clientId, style, onApprove, onError]);

    if (!clientId) {
        return <div className="text-xs text-lq-error" data-testid="paypal-missing-client-id">PayPal not configured.</div>;
    }
    if (!planId) {
        return <div className="text-xs text-lq-ink-500" data-testid="paypal-missing-plan">Coming soon — plan not configured yet.</div>;
    }
    return <div ref={ref} data-testid={`paypal-btn-${planId}`} />;
}
